import moment from 'moment-timezone';

import Config from 'common-src/config';

import { formatClock12 } from '../../helpers';
import { clinicVisitTimes } from '../Visit/helpers';

const MINUTES_BEFORE_BEHIND = 60;

export const zusPatientUrl = (zusUpid) =>
  zusUpid ? `${Config.ZUS_URL}/patients/${zusUpid}/overview` : null;

export const getApptDay = (appt) => `${appt?.departmentLocalDate || appt?.date || ''}`.slice(0, 10);

export const getApptTime = (appt) => `${appt?.departmentLocalTime || appt?.time || ''}`;

const toMinutes = (hhmm) => {
  const [hours, minutes] = `${hhmm || ''}`.split(':').map(Number);
  return hours * 60 + minutes;
};

export const sortByTime = (appts) =>
  [...appts].sort(
    (a, b) => getApptTime(a).localeCompare(getApptTime(b)) || `${a.id}`.localeCompare(`${b.id}`),
  );

export const isBehind = (appt) => {
  const zone = appt?.departmentTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const now = moment.tz(zone);
  const today = now.format('YYYY-MM-DD');
  const day = getApptDay(appt);
  if (day !== today) return day < today;

  const start = toMinutes(getApptTime(appt));
  const end = start + (appt?.durationMinutes || 0);
  return now.hours() * 60 + now.minutes() >= Math.max(start + MINUTES_BEFORE_BEHIND, end);
};

export const getOpeningVisit = (rows) => rows.find((row) => !isBehind(row)) ?? rows[0] ?? null;

export const formatVisitDate = (dayKey, todayKey) => {
  const date = moment(dayKey, 'YYYY-MM-DD');
  const sameYear = dayKey.slice(0, 4) === todayKey.slice(0, 4);
  return date.format(sameYear ? 'MMM D' : 'MMM D, YYYY');
};

export const getShownLastVisit = (appt) =>
  (appt?.seenByProvider ? appt.lastVisitWithProvider : null) ?? appt?.lastVisit ?? null;

export const getSeenBefore = (appt, todayKey) => {
  const shown = getShownLastVisit(appt);
  if (!shown) {
    return appt?.patient?.athenaIntegration?.athenaPatientId
      ? { tone: 'new', label: 'First visit with Mindspan' }
      : { tone: 'none', label: 'Visit history not in the HUD \u00b7 check Athena' };
  }

  const when = formatVisitDate(`${shown.date}`.slice(0, 10), todayKey);
  if (appt.seenByProvider) {
    return { tone: 'seen', label: `Last visit ${when} \u00b7 ${shown.visitType}` };
  }

  const withClinician = shown.clinician ? ` with ${shown.clinician}` : '';
  return {
    tone: 'new',
    label: `New to you \u00b7 last visit ${when} \u00b7 ${shown.visitType}${withClinician}`,
  };
};

const CPT_DESCRIPTION = {
  99205: 'Office visit, new patient, high complexity',
  99215: 'Office visit, established patient, high complexity',
  99483: 'Cognitive assessment and care plan services',
  96138: 'Test administration and scoring by technician, first 30 min',
  96136: 'Test administration and scoring by physician, first 30 min',
  96132: 'Neuropsychological evaluation by physician, first hour',
};

export const getCptTitle = (appt) =>
  appt?.cptCode ? `${appt.cptCode} ${CPT_DESCRIPTION[appt.cptCode] ?? ''}`.trim() : undefined;

export const slugify = (value) =>
  `${value ?? ''}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const getVisitDetails = (appt) =>
  [
    appt?.confirmed && 'Appointment confirmed',
    appt?.primaryInsurance,
    appt?.cptCode && `CPT ${appt.cptCode}`,
  ].filter(Boolean);

const CAREGIVER_PREFIX = {
  coming: 'Caregiver coming',
  by_phone: 'Caregiver joining by phone',
  not_confirmed: 'Caregiver not confirmed',
};

export const getCaregiverLine = (appt) => {
  const caregivers = appt?.caregivers ?? [];
  const lines = Object.keys(CAREGIVER_PREFIX)
    .map((status) => {
      const names = caregivers
        .filter((caregiver) => caregiver.status === status)
        .map(({ name, relationship }) =>
          relationship ? `${name} (${relationship.toLowerCase()})` : name,
        );
      return names.length ? `${CAREGIVER_PREFIX[status]}: ${names.join(', ')}` : null;
    })
    .filter(Boolean);
  if (!lines.length) return { text: 'No caregiver on file', tone: 'none' };
  const warn = caregivers.some((caregiver) => caregiver.status === 'not_confirmed');
  return { text: lines.join(' \u00b7 '), tone: warn ? 'warn' : 'ok' };
};

export const getDayDigest = (providerName, rows) => {
  if (!rows.length) return `${providerName} · No visits`;

  const visits = `${rows.length} ${rows.length === 1 ? 'visit' : 'visits'}`;
  const newToYou = rows.filter((row) => row.seenByProvider === false).length;
  const times = rows.map((row) => clinicVisitTimes(row));
  const ends = times.map((time) => time.end).sort();
  const withZone = (clock) => [formatClock12(clock), times[0].zone].filter(Boolean).join(' ');

  return [
    providerName,
    visits,
    newToYou > 0 ? `${newToYou} new to you` : null,
    `first ${withZone(times[0].start)}`,
    `done by ${withZone(ends[ends.length - 1])}`,
  ]
    .filter(Boolean)
    .join(' \u00b7 ');
};

export const isLowMoca = (result) => result?.maxScore === 30 && Math.round(result.score) < 20;

export const showsLabs = (labs) =>
  labs?.columns?.length > 0 || labs?.older?.length > 0 || labs?.other?.length > 0;

export const showsGroup = (group) =>
  group?.lines?.length > 0 || group?.older?.length > 0 || group?.others?.length > 0;

const isFirstVisitRecords = (records) => !!records && !records.ordered;

export const getMissingRecordLabels = (records) =>
  isFirstVisitRecords(records)
    ? [!showsLabs(records.labs) && 'Labs', !showsGroup(records.imaging) && 'Imaging'].filter(
        Boolean,
      )
    : [];

export const hasNoRecordRows = (records) =>
  isFirstVisitRecords(records) &&
  !showsLabs(records.labs) &&
  !showsGroup(records.imaging) &&
  !showsGroup(records.other);

export const getRecordsSearchedLine = (records) =>
  `Searched ${records.searched || 'Athena and Zus'}${
    records.asOf ? ` \u00b7 as of ${moment(records.asOf).format('MMM D, h:mm A')}` : ''
  }`;

export const intakeUnavailable = (intake, failed) => {
  if (failed) return 'Could not load the intake';
  if (intake === undefined) return 'Loading';
  if (intake === null || intake.status === 'not_sent') return 'No intake on file';
  if (intake.status !== 'completed') return 'No report yet';
  return intake.hasReport ? undefined : 'No report on file';
};
